/**
 * Standalone auth manager for non-React code like axios interceptors.
 * This is synced with the useAuth hook to maintain state consistency.
 */

import { APP_CONFIG } from '../constants';

const TOKEN_KEY = 'soar_access_token';

interface AuthData {
  token: string | null;
  accountId: string | null;
  onRefresh: (() => Promise<void | string>) | null;
}

const authContainer = {
  current: {
    token: localStorage.getItem(TOKEN_KEY),
    accountId: null,
    onRefresh: null,
  } as AuthData,
};

export const authManager = {
  get token() {
    return authContainer.current.token;
  },

  get accountId() {
    return authContainer.current.accountId;
  },

  get onRefresh() {
    return authContainer.current.onRefresh;
  },

  setAuth(data: Partial<AuthData>) {
    authContainer.current = { ...authContainer.current, ...data };
    if (data.token !== undefined) {
      if (data.token) {
        localStorage.setItem(TOKEN_KEY, data.token);
      } else {
        localStorage.removeItem(TOKEN_KEY);
      }
    }
  },

  async login(username = 'user', password = 'password') {
    // We use axios directly here to avoid circular dependency with apiService
    // eslint-disable-next-line import/no-extraneous-dependencies
    const axios = (await import('axios')).default;
    const loginUrl = `${APP_CONFIG.BASE_URL}auth/token`;
    try {
      const response = await axios.post(loginUrl, {
        username,
        password,
      });
      const { token } = response.data;
      this.setAuth({ token });
      return token;
    } catch (err: any) {
      console.error(`[SOAR Auth] Login error at ${loginUrl}:`, err.response?.status, err.message);
      this.setAuth({ token: null }); // Clear bad token
      throw err;
    }
  },
};
